import 'angular/core/services/security.service';
import {GRAPHQL, READ_REPO, WRITE_REPO} from '../services/constants';
import {UserType} from 'angular/utils/user-utils';
import {
    navigate,
    ThemeMode,
    ThemeService,
    ApplicationSettingsStorageService,
    RuntimeConfigurationContextService,
    NavigationContextService,
    SecurityService,
    AuthenticationService,
    SecurityContextService,
    LicenseService,
    service,
    User,
} from '@ontotext/workbench-api';

angular
    .module('graphdb.framework.security.controllers.user-settings', [
        'graphdb.framework.core.services.security-service',
    ])
    .controller('UserSettingsController', UserSettingsController);

UserSettingsController.$inject = [
    '$scope',
    'toastr',
    '$window',
    '$timeout',
    '$jwtAuth',
    '$rootScope',
    '$controller',
    'ModalService',
    '$translate',
    '$q',
    '$uibModal',
    'TrackingService',
];

function UserSettingsController($scope, toastr, $window, $timeout, $jwtAuth, $rootScope, $controller, ModalService, $translate, $q, $uibModal, TrackingService) {
    angular.extend(this, $controller('CommonUserCtrl', {$scope: $scope, passwordPlaceholder: 'security.new.password'}));

    // =========================
    // Private variables
    // =========================
    const securityService = service(SecurityService);
    const authenticationService = service(AuthenticationService);
    const securityContextService = service(SecurityContextService);
    const licenseService = service(LicenseService);

    const applicationSettingsStorageService = service(ApplicationSettingsStorageService);
    const navigationContextService = service(NavigationContextService);
    const themeService = service(ThemeService);
    const runtimeConfigurationContextService = service(RuntimeConfigurationContextService);

    /**
     * A timer task that will redirect back to the previous page after the user has been updated.
     * @type {Promise}
     */
    let waitBeforeRedirectBack;

    // =========================
    // Public variables
    // =========================

    $scope.mode = 'settings';
    $scope.showWorkbenchSettings = true;
    $scope.currentSystemTheme = themeService.getPreferredScheme();
    /**
     * The list of available theme mode options shown in the dropdown.
     * @type {{mode: ThemeMode, labelKey: string}[]}
     */
    $scope.themeModeOptions = [
        {mode: ThemeMode.system, labelKey: 'security.workbench.settings.theme.mode.system'},
        {mode: ThemeMode.light, labelKey: 'security.workbench.settings.theme.mode.light'},
        {mode: ThemeMode.dark, labelKey: 'security.workbench.settings.theme.mode.dark'},
    ];
    /**
     * @type {ApplicationSettings}
     */
    $scope.workbenchSettings = applicationSettingsStorageService.getApplicationSettings();
    $scope.themeChanged = false;
    $scope.saveButtonText = $translate.instant('common.save.btn');
    $scope.pageTitle = $translate.instant('view.settings.title');
    $scope.passwordPlaceholder = $translate.instant('security.new.password');
    $scope.grantedAuthorities = {
        [READ_REPO]: {},
        [WRITE_REPO]: {},
        [GRAPHQL]: {},
    };
    $scope.loader = false;
    /**
     * Allows to reset the password when updating the user.
     * @type {boolean}
     */
    $scope.noPassword = false;
    /**
     * If the button that opens the cookie policy modal should be visible or not.
     * @type {boolean}
     */
    $scope.showCookiePolicyLink = false;
    /**
     * Flag that controls the visibility of the cookie policy modal.
     * @type {boolean}
     */
    $scope.showCookieConsentModal = false;

    // =========================
    // Public functions
    // =========================

    $scope.hasEditRestrictions = function() {
        return true;
    };

    $scope.onThemeChange = function() {
        $scope.themeChanged = $scope.workbenchSettings.themeMode !== $scope.initialThemeMode;
    };

    $scope.isUser = function() {
        return $scope.userType === UserType.USER;
    };

    $scope.goBack = function() {
        const previousRoute = navigationContextService.getPreviousRoute();
        navigate(previousRoute ?? './');
    };

    $scope.updateCurrentUserData = function() {
        authenticationService.getCurrentUser()
            .then((authenticatedUser) => securityContextService.updateAuthenticatedUser(authenticatedUser));
    };

    $scope.redirectAdmin = function() {
        if (!$scope.currentUserData) {
            $rootScope.redirectToLogin();
        }
    };

    $scope.submit = function() {
        if ($scope.noPassword && $scope.userType === UserType.ADMIN) {
            ModalService.openSimpleModal({
                title: $translate.instant('security.save.admin.settings'),
                message: $translate.instant('security.admin.pass.unset'),
                warning: true,
            }).result.then(function() {
                $scope.updateUser();
            });
        } else {
            $scope.updateUser();
        }
    };

    $scope.updateUserHttp = function() {
        $scope.loader = true;
        const user = new User({
            username: $scope.user.username,
            password: $scope.noPassword ? '' : $scope.user.password || undefined,
            appSettings: $scope.user.appSettings,
        });
        securityService.updateAuthenticatedUser(user)
            .then(() => {
                toastr.success($translate.instant('security.user.updated', {name: $scope.user.username}));
                $scope.updateCurrentUserData();
                applicationSettingsStorageService.setApplicationSettings($scope.workbenchSettings);
                $scope.initialThemeMode = $scope.workbenchSettings.themeMode;
                $scope.themeChanged = false;
                if ($scope.workbenchSettings.isSystemMode()) {
                    themeService.applyColorScheme();
                } else {
                    themeService.setThemeMode($scope.workbenchSettings.themeMode);
                }
                goBackToPreviousView();
            }).catch((data) => {
                const msg = getError(data);
                toastr.error(msg, $translate.instant('common.error'));
            })
            .finally(() => {
                $scope.loader = false;
            });
    };

    $scope.updateUser = function() {
        if (!$scope.validateForm()) {
            return false;
        }
        $scope.updateUserHttp();
    };

    $scope.validateForm = function() {
        return $scope.validatePassword();
    };


    /**
     * Handler for the cookie policy modal close event, sets the flag to hide the modal.
     */
    $scope.onDialogClose = () => {
        $scope.showCookieConsentModal = false;
    };

    /**
     * Handler for the cookie policy link click event, sets the flag to show the modal.
     * @param $event
     */
    $scope.showCookiePolicy = ($event) => {
        // The button that triggers this handler is inside a form which has a submit property,
        // and we need to prevent that because it leads to a redirect to the home page.
        $event.preventDefault();
        $scope.showCookieConsentModal = true;
    };

    // =========================
    // Private functions
    // =========================

     const showCookiePolicyLink = () => {
         licenseService.updateLicenseStatus().then(() => {
            $scope.showCookiePolicyLink = TrackingService.isTrackingAllowed();
        });
    };

    const goBackToPreviousView = () => {
        waitBeforeRedirectBack = $timeout(function() {
            $scope.loader = false;
            const previousRoute = navigationContextService.getPreviousRoute();
            navigate(previousRoute ?? './');
        }, 2000);
    };

    const initUserData = function() {
        // Copy needed so that Cancel would work correctly, need to call updateCurrentUserData on OK
        $scope.user = {username: $scope.currentUserData.username};
        $scope.user.password = '';
        $scope.user.confirmpassword = '';
        $scope.user.external = $scope.currentUserData.external;
        $scope.user.appSettings = $scope.currentUserData.appSettings;
        // For backward compatibility
        if ($scope.user.appSettings['DEFAULT_VIS_GRAPH_SCHEMA'] === undefined) {
            $scope.user.appSettings['DEFAULT_VIS_GRAPH_SCHEMA'] = true;
        }
        $scope.userType = $scope.currentUserData.getUserType();
        $scope.grantedAuthorities = $scope.currentUserData.authorities.toUIModel();
        $scope.customRoles = $scope.currentUserData.authorities.getCustomRoles();
    };

    const processUser = () => {
        const principal = securityContextService.getAuthenticatedUser();
        $scope.currentUserData = principal.toUser();
        $scope.redirectAdmin();
        initUserData();
    };

    const onSystemThemeChanged = (theme) => {
        if (theme) {
            $scope.currentSystemTheme = theme;
        }
    };

    const subscriptions = [
        runtimeConfigurationContextService.onSystemThemeChanged(onSystemThemeChanged),
    ];

    // =========================
    // Subscriptions
    // =========================

    $scope.$on('$destroy', function() {
        $timeout.cancel(waitBeforeRedirectBack);
        subscriptions.forEach((unsubscribe) => unsubscribe());
    });

    // =========================
    // Initialization
    // =========================

    const initView = () => {
        processUser();
        $scope.workbenchSettings.themeMode = themeService.getThemeModeOrSystem();
        $scope.initialThemeMode = $scope.workbenchSettings.themeMode;
        showCookiePolicyLink();
    };

    initView();
}
